/* eslint-disable no-nested-ternary */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Flex,
  Badge,
  Text,
} from '@chakra-ui/react';
import { useIcms } from '../../../../../../hooks/useIcms';
import { LivroTipoSingular } from '../../../../../../types/icms';
import { formatCurrency, toNumber } from '../../../../../../utils/FormatFunctions';

interface LivrosTotaisDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  codiEmp: number;
  tipo: LivroTipoSingular;
  dtInicio: string;
  dtFim: string;
}

const tipoLabel: Record<LivroTipoSingular, string> = {
  entrada: 'Entrada',
  saida: 'Saída',
  servico: 'Serviço',
};

export function LivrosTotaisDrawer({
  isOpen,
  onClose,
  codiEmp,
  tipo,
  dtInicio,
  dtFim,
}: LivrosTotaisDrawerProps) {
  const { getLivroTotaisPorCfop, getLivroTotaisPorAcumulador } = useIcms();

  const { data: cfopData, isFetching: cfopLoading } = useQuery({
    queryKey: [
      'livro-totais-cfop',
      { codi_emp: codiEmp, tipo, dt_inicio: dtInicio, dt_fim: dtFim },
    ],
    queryFn: getLivroTotaisPorCfop,
    enabled: isOpen,
  });

  const { data: acuData, isFetching: acuLoading } = useQuery({
    queryKey: [
      'livro-totais-acumulador',
      { codi_emp: codiEmp, tipo, dt_inicio: dtInicio, dt_fim: dtFim },
    ],
    queryFn: getLivroTotaisPorAcumulador,
    enabled: isOpen,
  });

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="lg">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>Totais — Livro de {tipoLabel[tipo]}</DrawerHeader>
        <DrawerBody>
          <Tabs colorScheme="blue" variant="enclosed">
            <TabList>
              <Tab>Por CFOP</Tab>
              <Tab>Por Acumulador</Tab>
            </TabList>
            <TabPanels>
              <TabPanel px={0}>
                {cfopLoading ? (
                  <Flex justify="center" py={6}>
                    <Spinner color="primary" />
                  </Flex>
                ) : !cfopData || cfopData.length === 0 ? (
                  <Text color="gray.500" textAlign="center" py={6}>
                    Sem dados.
                  </Text>
                ) : (
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th>CFOP</Th>
                        <Th isNumeric>Notas</Th>
                        <Th isNumeric>V. Contábil</Th>
                        <Th isNumeric>Base ICMS</Th>
                        <Th isNumeric>ICMS</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {cfopData.map(row => (
                        <Tr key={row.cfop}>
                          <Td fontWeight="bold">{row.cfop}</Td>
                          <Td isNumeric>{row.qtd_notas}</Td>
                          <Td isNumeric>{formatCurrency(toNumber(row.total_vcontabil))}</Td>
                          <Td isNumeric>{formatCurrency(toNumber(row.total_base_icms))}</Td>
                          <Td isNumeric>{formatCurrency(toNumber(row.total_icms))}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                )}
              </TabPanel>
              <TabPanel px={0}>
                {acuLoading ? (
                  <Flex justify="center" py={6}>
                    <Spinner color="primary" />
                  </Flex>
                ) : !acuData || acuData.length === 0 ? (
                  <Text color="gray.500" textAlign="center" py={6}>
                    Sem dados.
                  </Text>
                ) : (
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Cód</Th>
                        <Th>Nome</Th>
                        <Th>Classif.</Th>
                        <Th isNumeric>Notas</Th>
                        <Th isNumeric>V. Contábil</Th>
                        <Th isNumeric>ICMS</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {acuData.map(row => (
                        <Tr key={row.codi_acu}>
                          <Td fontWeight="bold">{row.codi_acu}</Td>
                          <Td>{row.nome_acu}</Td>
                          <Td>
                            {row.classificacao ? (
                              <Badge colorScheme="blue" fontSize="9px">
                                {row.classificacao}
                              </Badge>
                            ) : (
                              '—'
                            )}
                          </Td>
                          <Td isNumeric>{row.qtd_notas}</Td>
                          <Td isNumeric>{formatCurrency(toNumber(row.total_vcontabil))}</Td>
                          <Td isNumeric>{formatCurrency(toNumber(row.total_icms))}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
